import { ZodSchema, ZodTypeDef } from "zod";

export function collectEntities<T extends { id: string | number }>(
  data: unknown,
  keys: string | string[],
  // 入力は parse 前の未検証値のため unknown(entityDefs の schema 型と揃える)
  schema: ZodSchema<T, ZodTypeDef, unknown>
): T[] {
  /* ---------------- Key セットを作成（単数⇔複数を両対応） ---------------- */
  const baseKeys = Array.isArray(keys) ? keys : [keys];
  const keySet = new Set<string>();

  for (const k of baseKeys) {
    keySet.add(k);                            // 元のキー
    if (!k.endsWith("s")) keySet.add(`${k}s`); // 単数 → 複数
    else keySet.add(k.slice(0, -1));           // 複数 → 単数
  }

  /* ---------------- 共有状態 ---------------- */
  const results: T[] = [];
  const seen = new Set<string | number>();     // id で一意化

  /* ---------------- 1 つの値をバリデートして push ---------------- */
  const tryAdd = (raw: unknown) => {
    const parsed = schema.safeParse(raw);
    if (parsed.success) {
      const ent = parsed.data;                // ← スキーマ外プロパティは除去済み
      if (!seen.has(ent.id)) {
        seen.add(ent.id);
        results.push(ent);
      }
    }
  };

  /* ---------------- 再帰 DFS ---------------- */
  const walk = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }

    if (node !== null && typeof node === "object") {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- typeof object 確認済みの unknown ツリーをキー参照するための境界
      const obj = node as Record<string, unknown>;

      /* 対象キーの値を処理 */
      for (const k of keySet) {
        const val = obj[k];
        if (val === undefined) continue;

        /* Users: [...] のように配列で来た場合 */
        if (Array.isArray(val)) {
          val.forEach(tryAdd);
        } else {
          tryAdd(val);
        }
      }

      /* 深い階層も探索 */
      Object.values(obj).forEach(walk);
    }
  };

  walk(data);
  return results;
}
